/**
 * KeyBuilder
 * -------------------------
 * A tiny helper to build **stable, comparable cache keys** for function calls
 * from a declared schema.
 *
 * ## What it does
 * - You **register** a schema for a function name (e.g. `"getUser"`).
 * - You **build** a key by passing that name plus the actual call arguments.
 * - The key is a single string you can use in caches, maps, metrics, etc.
 *
 * ## Key format
 * ```
 * <fnName>:<req1>|<req2>|...|<opt1>|<opt2>|...
 * ```
 * - Required fields (`req`) are always present and appear first, in the order
 *   declared by the schema.
 * - Optional fields (`opt`) follow required ones (if any optional fields exist
 *   in the schema). Each optional position is always emitted; when the caller
 *   does not supply a value, a placeholder is used.
 * - Field separator: `|` (see **Delimiters & safety** below)
 * - Section separator: `:` (between `fnName` and the payload)
 * - Missing optional marker: ASCII RS `\x1E` (non-printable, unlikely to collide)
 *
 * Examples:
 * ```ts
 * KeyBuilder.register("findUser", { req: ["id"], opt: ["region"] });
 * KeyBuilder.build("findUser", { id: 42 });              // "findUser:42|\x1E"
 * KeyBuilder.build("findUser", { id: 42, region: "eu" }); // "findUser:42|eu"
 *
 * KeyBuilder.register("search", {
 *   req: ["q"],
 *   opt: ["page", "pageSize"],
 *   defaults: { page: 1, pageSize: 25 }
 * });
 * KeyBuilder.build("search", { q: "foo" });               // "search:foo|1|25"
 * KeyBuilder.build("search", { q: "foo", page: 2 });       // "search:foo|2|25"
 * ```
 *
 * ## Schema
 * ```ts
 * type KeySchema = {
 *   // names of required argument keys (must be provided at build time, or via defaults)
 *   req: readonly string[];
 *   // names of optional argument keys (fixed positions, filled with a placeholder if absent)
 *   opt?: readonly string[];
 *   // default values merged before reading req/opt (shallow, by key)
 *   defaults?: Record<string, unknown>;
 * };
 * ```
 *
 * ### Registration
 * - `register(fnName, schema)` must be called once per `fnName`. Re-registering
 *   the same name throws.
 * - Schemas are stored process-wide in a static map.
 *
 * ### Building
 * - `build(fnName, args)` merges `schema.defaults` (if any) with `args`
 *   (args take precedence), validates that all `req` keys are present, then
 *   serializes values in a deterministic way (see **Normalization**).
 * - If a required key is missing after merging, an error is thrown.
 *
 * ### Normalization
 * - `string` → used as-is
 * - `number`/`boolean` → `String(v)`
 * - `null` → `"null"`
 * - other types (arrays, objects, dates, etc.) → `JSON.stringify(v)`
 *
 * Note: `JSON.stringify` preserves key order of the given object/array. For
 * stable keys, callers should pass consistently shaped objects (e.g. avoid
 * relying on non-deterministic key ordering upstream).
 *
 * ## Delimiters & safety
 * - No escaping is performed for strings. If your **string values** can contain
 *   `":"` or `"|"`, or the placeholder `\x1E`, you should pre-encode them (e.g.
 *   base64, URL-encode, or your own escaping) before passing to `build`, or
 *   choose non-string types that serialize safely for your use case.
 *
 * ## Errors
 * - `KeyBuilder: "<fnName>" already registered`
 * - `KeyBuilder: no schema for "<fnName>"`
 * - `KeyBuilder("<fnName>"): missing required "<key>"`
 *
 * ## Complexity
 * - Registration: O(1)
 * - Build: O(R + O) over the number of declared required/optional keys, plus
 *   the cost of `JSON.stringify` for non-primitive values.
 *
 * ## Typical use
 * ```ts
 * // 1) define once at startup
 * KeyBuilder.register("getWeather", {
 *   req: ["lat", "lon"],
 *   opt: ["units", "lang"],
 *   defaults: { units: "metric", lang: "en" }
 * });
 *
 * // 2) anywhere later
 * const key = KeyBuilder.build("getWeather", { lat: 12.34, lon: 56.78 });
 * // "getWeather:12.34|56.78|metric|en"
 * cache.get(key) ?? cache.set(key, await fetchWeather(...));
 * ```
 */
export class KeyBuilder {
    static SEP = '|';
    static MISSING = '\x1E';
    static schemas = new Map();
    static register(fnName, schema) {
        if (this.schemas.has(fnName))
            throw new Error(`KeyBuilder: "${fnName}" already registered`);
        this.schemas.set(fnName, schema);
    }
    static build(fnName, args) {
        const s = this.schemas.get(fnName);
        if (!s)
            throw new Error(`KeyBuilder: no schema for "${fnName}"`);
        const merged = Object.create(null);
        if (s.defaults)
            for (const k in s.defaults)
                merged[k] = s.defaults[k];
        for (const k in args)
            merged[k] = args[k];
        const req = s.req.map(k => {
            const v = merged[k];
            if (v === undefined)
                throw new Error(`KeyBuilder("${fnName}"): missing required "${k}"`);
            return KeyBuilder.norm(v);
        });
        const opt = (s.opt ?? []).map(k => {
            const v = merged[k];
            return v === undefined ? KeyBuilder.MISSING : KeyBuilder.norm(v);
        });
        return fnName + ':' + (opt.length ? req.concat(opt).join(KeyBuilder.SEP) : req.join(KeyBuilder.SEP));
    }
    static norm(v) {
        if (v === null)
            return 'null';
        switch (typeof v) {
            case 'string': return v;
            case 'number':
            case 'boolean': return String(v);
            default: return JSON.stringify(v);
        }
    }
}
//# sourceMappingURL=keyBuilder.js.map